import { defineStore } from 'pinia';
import { fetchWrapper } from '@/helpers';
import { useAlertStore } from '.';
import router from '@/router';
// import { connectToWebSocket, disConnectToWebSocket } from './socket.store';

const baseUrl = `${process.env.VUE_APP_VUE_API_URL}`;
const siteKey = process.env.VUE_APP_VUE_RECAPTCHA_V2_SITE_KEY;
// Sessions saved before SMTP moved to Admin > Integrations still carry the SMTP login: drop it from the browser.
const SMTP_KEYS = ['smtphost', 'smtpuser', 'smtppass', 'smtpsender', 'smtpport'];
const storedUser = () => {
    try {
        const user = JSON.parse(localStorage.getItem('user'));
        if (user && SMTP_KEYS.some((k) => k in user)) {
            SMTP_KEYS.forEach((k) => delete user[k]);
            localStorage.setItem('user', JSON.stringify(user));
        }
        return user;
    } catch (_) { return null; }
};
const defaultRegistrationMessage = "Currently, we are not accepting registrations. We'll reopen registration in the near future. Thank you for your patience.";
export const useAccountStore = defineStore('account', {
    state: () => ({
        user: storedUser(),
        totalLicences: 0,
        totalLicencesByToday: 0,
        returnUrl: null,
        reCaptchaSiteKey: siteKey,
        unread: 0,
        loading: false,
        transactions: [],
        commission: 0,
        siteTitle: '',
        tglink: '',
        siteInfo: '',
        notificationMessage: '',
        registrationDisabled: true,
        registrationMessage: defaultRegistrationMessage,
    }),
    actions: {
        async setUnread(count) {
            try {
                this.unread = count;
            } catch (error) {
                console.error(error)
            }
        },
        async getTgLink() {
            try{
                const res = await fetchWrapper.get(`${baseUrl}/account/tglink`)
                this.tglink = res.tglink;
            } catch(error) {
                console.error(error)
            }
        },
        async getCommission() {
            const alertStore = useAlertStore();
            try{
                const res = await fetchWrapper.get(`${baseUrl}/account/commission`)
                this.commission = res.commission;
            } catch(error) {
                alertStore.error(error);
            }
        },
        async download(fileURL) {
            const alertStore = useAlertStore();
            try{
                const res = await fetchWrapper.get(`${baseUrl}/account/download?url=${encodeURIComponent(fileURL)}`)
                return res.file
            } catch(error) {
                alertStore.error(error);
            }
        },
        async getMeta() {
            const alertStore = useAlertStore();
            try{
                const res = await fetchWrapper.get(`${baseUrl}/account/meta`)
                this.siteInfo = res.siteInfo;
                this.siteTitle = res.siteTitle;
                this.notificationMessage = res.notificationMessage;
                this.registrationDisabled = Boolean(res.registrationDisabled);
                this.registrationMessage = res.registrationMessage || defaultRegistrationMessage;
            } catch(error) {
                alertStore.error(error)
            }
        },
        async signin(email, password, recaptcha) {
            const alertStore = useAlertStore();
            try {
                this.loading = true;
                const res = await fetchWrapper.post(`${baseUrl}/account/signin`, { email, password, recaptcha });
                // chat.proproxies.co.uk is the staff chat app: customer accounts are not signed in there
                if (window.location.hostname.startsWith("chat.") && res.user?.role !== "admin") {
                    this.loading = false;
                    alertStore.error("Only ProProxies staff can sign in here. Customers sign in at login.proproxies.co.uk");
                    return;
                }
                this.user = res.user;
                localStorage.setItem('user', JSON.stringify(res.user));
                await router.push(this.returnUrl || '/buy-new-proxy');
                alertStore.success("You have successfully signed in.");
                if(res&&res.user&&res.user.username){
                    // connectToWebSocket(res.user.user_name)
                }
                this.loading = false;
            } catch (error) {
                this.loading = false;
                alertStore.error(error);
            }
        },
        async loadReferralTransactions() {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.get(`${baseUrl}/account/referral-transactions`);
                this.transactions = res.transactions;
            } catch (error) {
                alertStore.error(error);
            }
        },
        async getAccount() {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.get(`${baseUrl}/account/get`);
                this.user = res.user;
                // store user details and jwt in local storage to keep user logged in between page refreshes
                localStorage.setItem('user', JSON.stringify(res.user));

            } catch (error) {
                alertStore.error(error);
            }
        },
        async forgotpassword(values) {
            const alertStore = useAlertStore();
            const {
                email,
                recaptcha,
            } = values
            try {
                const user = await fetchWrapper.post(`${baseUrl}/account/forgot`, {
                    email,
                    role: 'user',
                    recaptcha,
                });
                if (user.message) {
                    alertStore.success("Please check your email.");
                    return true;
                }
            } catch (error) {
                alertStore.error(error);
                return false;
            }
        },
        async verifyid(values) {
            const alertStore = useAlertStore();
            const {
                email,
                resetid
            } = values
            try {
                const user = await fetchWrapper.post(`${baseUrl}/account/verifyid`, {
                    email,
                    resetid,
                });
                if (user.message === "Success") {
                    return true;
                } else {
                    return false
                }
            } catch (error) {
                alertStore.error(error);
                return false;
            }
        },
        async resetpassword(values) {
            const alertStore = useAlertStore();
            const {
                email, password, resetid
            } = values
            try {
                const user = await fetchWrapper.post(`${baseUrl}/account/resetpassword`, {
                    email,
                    password,
                    resetid,
                });
                if (user.message === "Success") {
                    alertStore.success("Reset password successed");
                } 
            } catch (error) {
                alertStore.error(error);
                return false;
            }
        },
        async signup(values, referral_code) {
            this.loading = true;
            const alertStore = useAlertStore();
            const {
                email,
                user_name,
                password,
                name,
                address,
                city,
                country,
                tglink
            } = values
            try {
                const user = await fetchWrapper.post(`${baseUrl}/account/signup`, {
                    email,
                    user_name,
                    password,
                    name,
                    address,
                    city,
                    country,
                    role: 'user',
                    referral: referral_code,
                    tglink: tglink
                });
                if (user.message === "User was registered successfully!") {
                    if (user.user.email === email) {
                        await router.push('/login');
                        alertStore.success("You have successfully signed up.");
                    }
                }
                this.loading = false;
                if (typeof window.gtag === 'function') {
                    window.gtag('event', 'user_registration', {
                        event_category: 'ecommerce',
                        event_label: 'User Registration',
                        value: 1,
                        debug_mode: true,
                    });
                } else {
                    console.warn('gtag not loaded yet');
                }
            } catch (error) {

                this.loading = false;
                alertStore.error(error);
                return false;
            }
        },

        async signout() {
            // disConnectToWebSocket(this.user.user_name)
            this.user = null;
            localStorage.removeItem('user');
            await router.push('/login');
            const alertStore = useAlertStore();
            alertStore.success("You have signed out.");
        },


        async updateAccountAll(values) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/account/update-all`, { values });
                this.user = res.user;
                // store user details and jwt in local storage to keep user logged in between page refreshes
                localStorage.setItem('user', JSON.stringify(res.user));
                alertStore.success("You have changed your Account infomation successfully");


            } catch (error) {
                alertStore.error(error);
            }
        },
        async updatePassword(old_password, new_password) {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.post(`${baseUrl}/account/updatePassword`, { old_password, new_password });
                // update pinia state

                this.user = res.user;
                // store user details and jwt in local storage to keep user logged in between page refreshes
                localStorage.setItem('user', JSON.stringify(res.user));
                alertStore.success("You have changed your password successfully");

                // await router.push(this.returnUrl || '/dashboard');

            } catch (error) {
                alertStore.error(error);
            }
        },
        async purchaseByBalanceFee(plan_uuid, quantity, username, ipAddress, city, modems_id, openVPN, code, isCoupon) {
            const alertStore = useAlertStore();
            try {
                let couponCode = null;
                if (isCoupon) {
                    couponCode = code;
                    const res = await fetchWrapper.post(`${baseUrl}/package/purchase-by-balance`, { plan_uuid, quantity, username, ipAddress, city, modems_id, openVPN, couponCode });
                    this.user = res.user;
                    updateLicenses()

                    localStorage.setItem('user', JSON.stringify(res.user));
                    await router.push(this.returnUrl || '/buy-new-proxy');
                    if (typeof window.gtag === 'function') {
                        window.gtag('event', 'purchase_click', {
                            event_category: 'ecommerce',
                            event_label: 'Purchase Button',
                            value: 1,
                            debug_mode: true,
                        });
                    } else {
                        console.warn('gtag not loaded yet');
                    }
                    return 1;
                }
            } catch (error) {
                if(error === "Insufficient balance"){
                    alertStore.error("Your balance is not enough for this order. Please top up and try again.");
                    return 0;   // the dialog then opens the top-up page
                }
                alertStore.error(error);
                return -1;
            }
        },
        async getLicenses() {
            const alertStore = useAlertStore();
            try {
                const res = await fetchWrapper.get(`${baseUrl}/license/get-license-counts`)
                this.totalLicences = res.licences.totalLicences
                this.totalLicencesByToday = res.licences.totalLicencesByToday
            } catch (error) {
                alertStore.error(error.message)
            }
        },

    },
});

export const updateAccount = (key, value) => {
    useAccountStore().updateAccountAll(key, value)
}
export const updateLicenses = () => {
    useAccountStore().getLicenses()
}
